/**
 * @fileoverview "Add a to-do" and "Plant something", side by side (E25; owner, 30 Oct: "maybe its more like add a to do
 * is one button, plant something is another"; Fable's mock-up v2, screens 1 and 2). Two dashed buttons of one size, a
 * thumb and a half tall, in the primary colour: a dashed square with a plus and "Add a to-do" (the reminder sheet), and
 * a sprout and "Plant something" (the Plant something sheet). She chooses before she types, and never meets the other
 * path again. Not in the menu (owner, 30 Oct).
 * The buttons read "To-do" and "Plant" at the normal text size (B125; owner, 30 Oct, on B124's shrunk text: "its awful"),
 * short enough to sit side by side, equal width and height, on one line from 320px on Today and on a bed's card.
 * Each button's aria-label keeps the full words ("Add a to-do", "Plant something") for screen readers.
 *
 * The card variant is Today's: the Today group's first row, on the page's cards' white (as AddTodoRow's card was, B60).
 * The row variant is a bed's card's, under its "Your to-dos".
 *
 * Where: TodoGroups (Today, the card variant), OwnTodosRow (a bed's card). A plant's card keeps "Add a to-do" alone
 * (AddTodoRow).
 * API: none.
 * Words (en/es): todo.own.addShort, todo.own.plant.buttonShort (the labels); todo.own.add, todo.own.plant.button (the aria-labels).
 *
 * @module own-todos/AddAndPlantButtons
 */

import React from 'react';
import { Plus, Sprout } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** AddAndPlantButtons' props. */
interface AddAndPlantButtonsProps {
  /** Opens the reminder sheet. */
  onAdd: () => void;
  /** Opens the Plant something sheet. */
  onPlant: () => void;
  /** "card": Today's, on a card's white; "row": a bed's card's. */
  variant?: 'card' | 'row';
  /** More classes (its margin on the page). */
  className?: string;
}

/** One dashed button: a thumb and a half tall (at least 44px), its short label on one line at the normal text size in the primary colour, centred with room at the sides. */
const BUTTON = 'flex min-h-12 min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border-[1.5px] border-dashed px-3 py-2 text-center text-[0.9375rem] font-semibold leading-tight text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * A label with its hyphens made non-breaking (kept from before the labels were held to one line; harmless with
 * whitespace-nowrap).
 *
 * @param text - The label.
 * @returns The label with U+2011 for each hyphen.
 */
const keepHyphens = (text: string): string => text.replace(/-/g, '‑');

/**
 * The two buttons.
 *
 * @param props - AddAndPlantButtonsProps.
 */
export const AddAndPlantButtons: React.FC<AddAndPlantButtonsProps> = ({ onAdd, onPlant, variant = 'card', className }) => {
  const { reactTranslate: t } = useTranslation();
  const frame = variant === 'card' ? 'border-primary/40 bg-card' : 'border-border';
  // Both variants: two equal columns; the short labels fit side by side from 320px.
  return (
    <div className={cn('grid grid-cols-2 gap-2', className)}>
      <button type="button" onClick={onAdd} aria-label={t('todo.own.add')} className={cn(BUTTON, frame)}>
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-[1.5px] border-dashed border-current" aria-hidden="true">
          <Plus className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
        <span>{keepHyphens(t('todo.own.addShort'))}</span>
      </button>
      <button type="button" onClick={onPlant} aria-label={t('todo.own.plant.button')} className={cn(BUTTON, frame)}>
        <Sprout className="h-[1.125rem] w-[1.125rem] shrink-0" aria-hidden="true" />
        <span>{keepHyphens(t('todo.own.plant.buttonShort'))}</span>
      </button>
    </div>
  );
};
